import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink, Type, Info, BookOpen, Calendar, Grid, Sparkles, Thermometer, Star, Check } from 'lucide-react';
import SEO from './SEO';
import Header from './Header';

const SylloLandingPage = () => {
  return (
    <div className="flex flex-col min-h-screen">
      <SEO 
        title="Syllo Reddit Game: How to Play, Daily Hints & Answers" 
        description="Master the Syllo reddit game with our daily hints and answers. Learn how to play Syllo, find the subreddit, and get today's solutions."
        url={`${window.location.origin}/syllo`}
      />

      <Header 
        date={new Date().toISOString()} 
        title="Syllo Game Guide" 
        gameName="Syllo Reddit" 
        heroTitle="Syllo Reddit Game: Daily Hints & Guide"
      />

      <main className="flex-grow max-w-5xl mx-auto px-4 w-full py-12">
        <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-gray-100 mb-12">
          <div className="flex items-center gap-6 mb-8">
            <div className="bg-brand-blue/10 p-4 rounded-2xl">
              <img 
                src={process.env.PUBLIC_URL + '/Syllo-logo.png'} 
                alt="Syllo Logo" 
                className="w-20 h-20 object-contain"
              />
            </div>
            <div>
              <h1 className="text-4xl font-black text-gray-900 mb-2">Syllo</h1>
              <p className="text-xl text-gray-600">The Syllable Merging Word Puzzle</p>
            </div>
          </div>

          <div className="prose prose-lg max-w-none text-gray-600 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Info className="w-6 h-6 text-brand-blue" />
              What is Syllo?
            </h2>
            <p>
              <strong>Syllo</strong> is a unique daily word game that challenges your vocabulary and pattern recognition. It has a dedicated following on the <strong>Syllo Reddit</strong> community where players share their daily streaks and solve times.
            </p>
            <p>
              Unlike traditional crosswords, Syllo focuses on <em>syllables</em>. You are given a set of clues and a pool of syllables, and your task is to combine them correctly to form the target words.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-4 flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-brand-blue" />
              How to Play Syllo
            </h2>
            <p>Mastering Syllo requires a mix of logic and linguistics:</p>
            <ul className="list-disc pl-6 space-y-3">
              <li><strong>Read the Clues:</strong> You'll typically have 5 clues to solve in a single session.</li>
              <li><strong>Scan the Syllables:</strong> Look at the available syllable blocks at the bottom of the screen.</li>
              <li><strong>Combine & Solve:</strong> Tap syllables to build words that match the clues. Each syllable is used exactly once.</li>
              <li><strong>Increasing Difficulty:</strong> The puzzles get progressively more difficult throughout the week, culminating in a tough Sunday challenge!</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-4 flex items-center gap-2">
              <ExternalLink className="w-6 h-6 text-brand-blue" />
              Official Links & Community
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 not-prose">
              <a 
                href="https://www.reddit.com/r/syllo/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center justify-between p-6 rounded-2xl bg-brand-blue text-white shadow-lg shadow-blue-100 hover:bg-blue-600 transition-all group md:col-span-2"
              >
                <div className="flex flex-col">
                  <span className="text-xs uppercase font-bold opacity-80 mb-1">Play the game & join the talk</span>
                  <span className="text-xl font-black">Official Syllo Reddit</span>
                </div>
                <ExternalLink className="w-8 h-8 opacity-50 group-hover:opacity-100 group-hover:scale-110 transition-all" />
              </a>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-12 mt-12">
            <h2 className="text-3xl font-black text-gray-900 mb-8 text-center uppercase tracking-tight">Today's Syllo Help</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Link 
                to="/todays-syllo-clues-answers"
                className="flex flex-col items-center justify-center p-8 bg-brand-blue text-white rounded-3xl hover:bg-blue-600 transition-all hover:scale-[1.02] shadow-xl shadow-blue-100 group text-center"
              >
                <Type className="w-12 h-12 mb-4 opacity-80 group-hover:scale-110 transition-transform" />
                <span className="text-2xl font-black mb-2">Today's Hints</span>
                <span className="opacity-90">Get clues & answers for Jan 17, 2026</span>
                <ArrowRight className="w-6 h-6 mt-4" />
              </Link>

              <Link 
                to="/syllo-archive"
                className="flex flex-col items-center justify-center p-8 bg-white border-2 border-gray-100 text-gray-900 rounded-3xl hover:border-brand-blue/20 hover:bg-gray-50 transition-all hover:scale-[1.02] shadow-lg group text-center"
              >
                <Calendar className="w-12 h-12 mb-4 text-brand-blue opacity-80 group-hover:scale-110 transition-transform" />
                <span className="text-2xl font-black mb-2">Full Archive</span>
                <span className="text-gray-500">Browse all past Syllo puzzles</span>
                <ArrowRight className="w-6 h-6 mt-4 text-brand-blue" />
              </Link>
            </div>
          </div>
        </div>

        {/* Interlinking Section */}
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-8">
            <div className="h-8 w-2 bg-brand-yellow rounded-full" />
            <h2 className="text-3xl font-black text-gray-900 uppercase tracking-tight">Other Popular Games</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link 
              to="/pocket-grids"
              className="group flex items-center gap-6 p-6 bg-white rounded-2xl border border-gray-100 shadow-sm hover:border-brand-blue/20 transition-all"
            >
              <div className="bg-blue-50 p-3 rounded-xl group-hover:scale-110 transition-transform">
                <img src={process.env.PUBLIC_URL + '/pocket-grids-logo.png'} className="w-10 h-10 object-contain" alt="Pocket Grids" />
              </div>
              <div className="flex-grow">
                <h3 className="font-bold text-gray-900 text-lg">Pocket Grids Reddit Guide</h3>
                <p className="text-sm text-gray-500">The ultimate daily crossword challenge</p>
              </div>
              <ArrowRight className="w-5 h-5 text-gray-300 group-hover:text-brand-blue transition-colors" />
            </Link>

            <Link 
              to="/hot-and-cold"
              className="group flex items-center gap-6 p-6 bg-white rounded-2xl border border-gray-100 shadow-sm hover:border-brand-blue/20 transition-all"
            >
              <div className="bg-orange-50 p-3 rounded-xl group-hover:scale-110 transition-transform">
                <img src={process.env.PUBLIC_URL + '/hotandcold-logo.png'} className="w-10 h-10 object-contain" alt="Hot and Cold" />
              </div>
              <div className="flex-grow">
                <h3 className="font-bold text-gray-900 text-lg">Hot and Cold Reddit Guide</h3>
                <p className="text-sm text-gray-500">The semantic temperature game</p>
              </div>
              <ArrowRight className="w-5 h-5 text-gray-300 group-hover:text-brand-blue transition-colors" />
            </Link>

          </div>
        </section>

        {/* Quick Help Links */}
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Link to="/todays-pocket-grids-clues-answers" className="flex items-center gap-2 p-4 bg-white rounded-xl border border-gray-100 shadow-sm hover:bg-gray-50 transition-colors font-bold text-gray-700">
            <Grid className="w-5 h-5 text-brand-blue" />
            Today's Pocket Grids Hints
          </Link>
          <Link to="/todays-hot-and-cold-clues-answers" className="flex items-center gap-2 p-4 bg-white rounded-xl border border-gray-100 shadow-sm hover:bg-gray-50 transition-colors font-bold text-gray-700">
            <Thermometer className="w-5 h-5 text-orange-600" />
            Today's Hot and Cold Hints
          </Link>
          <Link to="/archives" className="flex items-center gap-2 p-4 bg-white rounded-xl border border-gray-100 shadow-sm hover:bg-gray-50 transition-colors font-bold text-gray-700">
            <Sparkles className="w-5 h-5 text-brand-blue" />
            All Puzzle Archives
          </Link>
          <Link to="/daily-guess-solver" className="flex items-center gap-2 p-4 bg-white rounded-xl border border-gray-100 shadow-sm hover:bg-gray-50 transition-colors font-bold text-gray-700">
            <Check className="w-5 h-5 text-green-600" />
            DailyGuess Solver
          </Link>
        </section>
      </main>
    </div>
  );
};

export default SylloLandingPage;
